// Menu Data - Comprehensive navigation for Fridge.com
// Text-only menu items (no icons) - professional like Samsung, Best Buy, Home Depot
//
// SEO strategy: Header links appear on every page (~90,500 live URLs — see docs/Page-Types-And-Templates.md).
// Every URL here gets site-wide PageRank. Be deliberate:
// - Hub page URLs (/<slug>) preferred over collection URLs where hubs exist
// - Hub pages have sr-only LLM content, JSON-LD schemas, FAQs — they're the SEO targets
// - Collection URLs (/collections/<slug>) used only when no hub page exists
// - Header = highest-volume search terms + primary customer navigation
// - All URLs verified against Supabase hub_pages + collections tables

// Types
export interface MenuItem {
  title: string
  href: string
  description: string
}

export interface MenuSection {
  label: string
  items: MenuItem[]
}

export interface FeaturedBlock {
  title: string
  description: string
  image: string
  href: string
  cta: string
}

export interface MenuCategory {
  trigger: string
  href: string // Link for "Shop All" on mobile
  sections: MenuSection[]
  featured?: FeaturedBlock
}

// Menu Data - Organized by how customers shop + SEO value
// Authority Architecture (6 Pillars): Fridges | Freezers | Brands | Guides | Local | Tools
export const menuData: MenuCategory[] = [
  // ─────────────────────────────────────────────
  // 1. FRIDGES — Core product searches + Specialty & Filters
  // ─────────────────────────────────────────────
  {
    trigger: 'Fridges',
    href: '/collections/refrigerators',
    sections: [
      {
        label: 'By Style',
        items: [
          {
            title: 'Best Refrigerators 2026',
            href: '/best-refrigerators',
            description: 'Top-rated picks this year',
          },
          {
            title: 'French Door Refrigerators',
            href: '/collections/french-door-refrigerators',
            description: 'Wide shelves, easy access',
          },
          {
            title: 'Side-by-Side Refrigerators',
            href: '/side-by-side-refrigerators',
            description: 'Equal fridge & freezer space',
          },
          {
            title: 'Top Freezer Refrigerators',
            href: '/top-freezer-refrigerators',
            description: 'Classic, efficient design',
          },
          {
            title: 'Bottom Freezer Refrigerators',
            href: '/bottom-freezer-refrigerators',
            description: 'Fresh food at eye level',
          },
          {
            title: 'All Refrigerators',
            href: '/collections/refrigerators',
            description: 'Browse our complete collection',
          },
        ],
      },
      {
        label: 'By Feature',
        items: [
          {
            title: 'Counter-Depth Refrigerators',
            href: '/counter-depth-refrigerators',
            description: 'Sleek, built-in look',
          },
          {
            title: 'Built-In Refrigerators',
            href: '/built-in-refrigerators',
            description: 'Seamless kitchen integration',
          },
          {
            title: 'Smart Refrigerators',
            href: '/smart-fridges',
            description: 'WiFi, touchscreen, connected',
          },
          {
            title: 'Energy Efficient Refrigerators',
            href: '/energy-efficient-refrigerators',
            description: 'Lower bills, less impact',
          },
          {
            title: 'Large Capacity Refrigerators',
            href: '/large-capacity-refrigerators',
            description: '25+ cu. ft. for big families',
          },
          {
            title: 'Quiet Refrigerators',
            href: '/quiet-refrigerators',
            description: 'Whisper-quiet operation',
          },
        ],
      },
      {
        label: 'Specialty',
        items: [
          {
            title: 'Wine Coolers',
            href: '/collections/wine-fridges',
            description: 'Perfect temperature for wine',
          },
          {
            title: 'Beverage Coolers',
            href: '/collections/beverage-coolers',
            description: 'Keep drinks cold & ready',
          },
          {
            title: 'Beer Fridges',
            href: '/collections/beer-fridges',
            description: 'Dedicated drink storage',
          },
          {
            title: 'Kegerators',
            href: '/kegerators',
            description: 'Draft beer at home',
          },
          {
            title: 'Ice Makers',
            href: '/ice-makers',
            description: 'Fresh ice on demand',
          },
          {
            title: 'Outdoor Refrigerators',
            href: '/outdoor-refrigerators',
            description: 'Weather-resistant cooling',
          },
          {
            title: 'Mini Fridges',
            href: '/mini-fridges',
            description: 'Perfect for small spaces',
          },
        ],
      },
      {
        label: 'Filters',
        items: [
          {
            // FRIDGE-723 — the finder on the filter family home: fridge model
            // number OR filter part number in, the right filter out.
            title: 'Find Your Filter',
            href: '/filters#filter-finder',
            description: 'By fridge model or filter number',
          },
          {
            // FRIDGE-722 — the per-model hubs: one page per OEM filter number
            // (XWFE, LT1000P, DA29-00020B …); the index always serves.
            title: 'Filters by Part Number',
            href: '/filters/models',
            description: 'OEM + compatible options, prices compared',
          },
          {
            title: 'Refrigerator Water Filters',
            href: '/filters#water-filters',
            description: 'Replacement water filters',
          },
          {
            title: 'Refrigerator Air Filters',
            href: '/filters/refrigerator-air-filters',
            description: 'Deodorizer & air purification',
          },
          {
            title: 'Ice Maker Filters',
            href: '/filters/ice-maker-filters',
            description: 'Clean ice filtration',
          },
          {
            title: 'Best Aftermarket',
            href: '/filters/best-aftermarket-refrigerator-water-filters',
            description: 'Save 60-80% Vs. OEM',
          },
          {
            title: 'OEM Vs. Aftermarket',
            href: '/filters/oem-vs-aftermarket-refrigerator-water-filters',
            description: 'Compare safety and cost',
          },
          {
            title: 'All Filters',
            href: '/filters',
            description: 'Browse all filter brands & types',
          },
        ],
      },
    ],
    featured: {
      title: 'Refrigerator Buying Guide',
      description: 'Everything you need to know before buying your next fridge',
      image: `/menu-images/fridges-featured.jpg`,
      href: '/refrigerator-buying-guide',
      cta: 'Read the Guide',
    },
  },

  // ─────────────────────────────────────────────
  // 2. FREEZERS — Core product searches + Commercial
  // ─────────────────────────────────────────────
  {
    trigger: 'Freezers',
    href: '/collections/freezers',
    sections: [
      {
        label: 'By Type',
        items: [
          {
            title: 'Best Freezers 2026',
            href: '/best-freezers',
            description: 'Top-rated picks this year',
          },
          {
            title: 'Upright Freezers',
            href: '/collections/upright-freezers',
            description: 'Easy access, organized shelves',
          },
          {
            title: 'Chest Freezers',
            href: '/collections/chest-freezers',
            description: 'Maximum storage capacity',
          },
          {
            title: 'Deep Freezers',
            href: '/collections/deep-freezers',
            description: 'Long-term bulk storage',
          },
          {
            title: 'Small Chest Freezers',
            href: '/collections/small-chest-freezers',
            description: 'Compact bulk storage',
          },
          {
            // FRIDGE-1425 (10/08/2026): the compact-freezers head consolidated into
            // mini-freezers (its 301 target); the menu links the survivor.
            title: 'Mini & Compact Freezers',
            href: '/collections/mini-freezers',
            description: 'Extra space anywhere',
          },
        ],
      },
      {
        label: 'By Feature',
        items: [
          {
            title: 'Garage Freezers',
            href: '/collections/garage-freezers',
            description: 'Built for temperature swings',
          },
          {
            title: 'Energy Efficient Freezers',
            href: '/energy-efficient-freezers',
            description: 'Lower bills, less impact',
          },
          {
            title: 'Built-In Freezers',
            href: '/collections/built-in-freezers',
            description: 'Seamless integration',
          },
          {
            title: 'Outdoor Freezers',
            href: '/collections/outdoor-freezers',
            description: 'Weather-resistant design',
          },
          {
            title: 'Freezers Under $500',
            href: '/collections/freezers-under-500',
            description: 'Budget-friendly options',
          },
          {
            title: 'Freezers Under $1,000',
            href: '/collections/freezers-under-1000',
            description: 'Great value picks',
          },
        ],
      },
      {
        label: 'Commercial',
        items: [
          {
            title: 'Commercial Refrigerators',
            href: '/commercial-refrigerators',
            description: 'Reach-in, walk-in, prep tables',
          },
          {
            title: 'Commercial Freezers',
            href: '/commercial-freezers',
            description: 'Chest, upright, walk-in',
          },
          {
            title: 'Commercial Display Cases',
            href: '/collections/commercial-display-cases',
            description: 'Merchandise coolers',
          },
          {
            title: 'Commercial Bar Coolers',
            href: '/collections/commercial-bar-coolers',
            description: 'Back bar refrigeration',
          },
        ],
      },
    ],
    featured: {
      title: 'Freezer Buying Guide',
      description: 'Which freezer type is right for your needs?',
      image: `/menu-images/freezers-featured.jpg?v=3`,
      href: '/freezer-buying-guide',
      cta: 'Read the Guide',
    },
  },

  // ─────────────────────────────────────────────
  // 3. BRANDS — Brand directory + Comparisons
  // ─────────────────────────────────────────────
  {
    trigger: 'Brands',
    href: '/brands',
    sections: [
      {
        label: 'Top Brands',
        items: [
          {
            title: 'Samsung Refrigerators',
            href: '/samsung-refrigerators',
            description: 'Smart tech, Family Hub',
          },
          {
            title: 'LG Refrigerators',
            href: '/lg-refrigerators',
            description: 'InstaView, craft ice',
          },
          {
            title: 'Whirlpool Refrigerators',
            href: '/whirlpool-refrigerators',
            description: 'Reliable, trusted name',
          },
          {
            title: 'GE Refrigerators',
            href: '/ge-refrigerators',
            description: 'American innovation',
          },
          {
            title: 'Frigidaire Refrigerators',
            href: '/frigidaire-refrigerators',
            description: 'Quality at every price',
          },
          {
            title: 'KitchenAid Refrigerators',
            href: '/kitchenaid-refrigerators',
            description: 'Premium kitchen design',
          },
        ],
      },
      {
        label: 'More Brands',
        items: [
          {
            title: 'Bosch Refrigerators',
            href: '/collections/bosch-refrigerators',
            description: 'German engineering',
          },
          {
            title: 'Maytag Refrigerators',
            href: '/maytag-refrigerators',
            description: 'Built to last',
          },
          {
            title: 'Hisense Refrigerators',
            href: '/collections/hisense-refrigerators',
            description: 'Smart features, great value',
          },
          {
            title: 'Midea Refrigerators',
            href: '/midea-refrigerators',
            description: 'Affordable innovation',
          },
          {
            title: 'Amana Refrigerators',
            href: '/amana-refrigerators',
            description: 'Simple, dependable',
          },
          {
            title: 'Danby Refrigerators',
            href: '/danby-refrigerators',
            description: 'Compact & specialty',
          },
          {
            title: 'All Brands →',
            href: '/brands',
            description: 'Browse all 130+ brands',
          },
        ],
      },
      {
        label: 'Comparisons',
        items: [
          {
            title: 'Refrigerator Comparisons',
            href: '/refrigerator-comparisons',
            description: 'See side-by-side spec battles',
          },
          {
            title: 'Freezer Comparisons',
            href: '/freezer-comparisons',
            description: 'Compare top freezer models',
          },
          // Link-audit 07/20/2026: the three /compare/<brand>-vs-<brand> URLs
          // return 410 Gone on production (deliberately retired) — replaced
          // with verified-200 money surfaces (curl-checked on fridge.com).
          {
            title: 'Compare Refrigerators Side-by-Side',
            href: '/compare',
            description: 'Any two models, spec by spec',
          },
          {
            title: 'Refrigerator Deals',
            href: '/deals',
            description: 'Live price drops right now',
          },
        ],
      },
    ],
    featured: {
      title: 'Compare Refrigerators',
      description: 'See how top brands and models stack up side by side',
      image: `/menu-images/brands-featured.jpg`,
      href: '/refrigerator-comparisons',
      cta: 'Compare Now',
    },
  },

  // ─────────────────────────────────────────────
  // 4. GUIDES — Best-Of 2026, Buying Guides, Blog index (NEW)
  // ─────────────────────────────────────────────
  {
    trigger: 'Guides',
    href: '/blogs/news',
    sections: [
      {
        label: 'Buying Guides',
        items: [
          {
            title: 'Refrigerator Buying Guide',
            href: '/refrigerator-buying-guide',
            description: 'Everything you need to know',
          },
          {
            title: 'Freezer Buying Guide',
            href: '/freezer-buying-guide',
            description: 'Which type is right for you?',
          },
          {
            title: 'Sizing Guide',
            href: '/refrigerator-sizing-guide',
            description: 'How to measure and fit perfectly',
          },
          {
            title: 'Energy Efficiency Guide',
            href: '/energy-efficiency-guide',
            description: 'Save money on your electric bill',
          },
        ],
      },
      {
        label: 'Best Of 2026',
        items: [
          {
            title: 'Best Refrigerators 2026',
            href: '/best-refrigerators',
            description: 'Top-rated picks this year',
          },
          {
            title: 'Best Freezers 2026',
            href: '/best-freezers',
            description: 'Highest capacity and reliability',
          },
          {
            title: 'Best Specialty Appliances',
            href: '/best-specialty-appliances',
            description: 'Wine, beverage & ice makers',
          },
        ],
      },
      {
        label: 'Resources',
        items: [
          {
            title: 'Repair Vs. Replace',
            href: '/tools/refrigerator-repair-vs-replace-calculator',
            description: 'When it makes sense to buy new',
          },
          {
            title: 'Warranty Comparison',
            href: '/tools/refrigerator-warranty-comparison',
            description: 'Compare plans & coverage',
          },
          {
            title: 'Recall Checker',
            href: '/tools/refrigerator-recall-checker',
            description: 'Check for active safety recalls',
          },
          // Owner directive 07/20 (item 13): header must represent ALL major
          // page types. /financing and /warranty (guide hubs) were footer-only;
          // surfaced here too. Both verified 200 on the live server.
          {
            title: 'Financing Guide',
            href: '/financing',
            description: 'Ways to pay over time',
          },
          {
            title: 'Warranty Guide',
            href: '/warranty',
            description: 'What coverage actually protects',
          },
          {
            title: 'Recycling Guide',
            href: '/recycling-guide',
            description: 'How to recycle your old fridge',
          },
        ],
      },
      {
        label: 'Kitchen News',
        items: [
          {
            title: 'Latest Articles',
            href: '/blogs/news',
            description: 'Browse our entire blog',
          },
          {
            title: 'Appliance Trends',
            href: '/blogs/news/tags/trends',
            description: 'What is new in the kitchen',
          },
          {
            title: 'Maintenance Tips',
            href: '/blogs/news/tags/maintenance',
            description: 'Keep your fridge running longer',
          },
        ],
      },
    ],
    featured: {
      title: 'Find Your Perfect Fridge Quiz',
      description: 'Answer five questions to explore refrigerator and freezer groups.',
      image: `/menu-images/more-featured.jpg?v=2`,
      href: '/tools/find-your-fridge',
      cta: 'Take the Quiz',
    },
  },

  // ─────────────────────────────────────────────
  // 5. LOCAL — ZIP-level energy finders, Utility rebates (NEW)
  // ─────────────────────────────────────────────
  {
    trigger: 'Local',
    href: '/local',
    sections: [
      {
        label: 'Fridge Finder',
        items: [
          {
            title: 'Local Fridge Finder',
            href: '/local',
            description: 'Rates by state, city & ZIP',
          },
          {
            title: 'California Rates',
            href: '/local/california',
            description: 'Fridge Energy Data for CA',
          },
          {
            title: 'Texas Rates',
            href: '/local/texas',
            description: 'Fridge Energy Data for TX',
          },
          {
            title: 'Florida Rates',
            href: '/local/florida',
            description: 'Fridge Energy Data for FL',
          },
          {
            title: 'New York Rates',
            href: '/local/new-york',
            description: 'Fridge Energy Data for NY',
          },
          {
            title: 'All 50 States',
            href: '/local',
            description: 'View the national map',
          },
        ],
      },
      {
        label: 'Utility Rebates',
        items: [
          {
            title: 'Find Local Rebates',
            href: '/rebates',
            description: 'Rebate programs near you',
          },
          {
            title: 'Energy Star Programs',
            href: '/collections/energy-star-appliances',
            description: 'Shop eligible appliances',
          },
          {
            title: 'Rebate FAQs',
            href: '/rebates#faq',
            description: 'How to claim your money',
          },
        ],
      },
      {
        label: 'Energy Data',
        items: [
          {
            title: 'State Energy Rankings',
            href: '/local',
            description: 'Which states pay the most?',
          },
          {
            title: 'Energy Cost Calculator',
            href: '/tools/energy-cost-calculator',
            description: 'Estimate your appliance running costs',
          },
        ],
      },
    ],
    featured: {
      title: 'Local Utility Rebates',
      description: 'Find cash back programs and energy savings in your ZIP code.',
      image: `/menu-images/specialty-featured.jpg`,
      href: '/rebates',
      cta: 'Find Rebates',
    },
  },

  // ─────────────────────────────────────────────
  // 6. DATA — the Fridge.com data portal (chrome-upgrade zone 3)
  //    All links are public, indexed routes (never internal/admin); every one
  //    verified live: /data, /data/energy-index, /recalls, /rebates,
  //    /recycling-guide. Non-collection links, so they pass the chrome gate.
  // ─────────────────────────────────────────────
  {
    trigger: 'Data',
    href: '/data',
    sections: [
      {
        label: 'Indexes & Research',
        items: [
          {
            title: 'Appliance Price Index',
            href: '/data',
            description: 'Matched-model refrigerator price trends',
          },
          {
            title: 'Energy-Efficiency Index',
            href: '/data/energy-index',
            description: 'Certified annual energy use by type',
          },
          // Owner directive 07/20 (item 13): the Press section (original research &
          // reports) is a major page type; surfaced in the header here where it
          // fits topically alongside the data indexes. Verified 200 live.
          {
            title: 'Press & Research',
            href: '/press',
            description: 'Original studies, data & media coverage',
          },
        ],
      },
      {
        label: 'Safety & Savings',
        items: [
          {
            title: 'Recalls',
            href: '/recalls',
            description: 'Refrigerator & freezer safety recalls',
          },
          {
            title: 'Utility Rebates',
            href: '/rebates',
            description: 'Cash-back programs in your area',
          },
          {
            title: 'Recycling Guide',
            href: '/recycling-guide',
            description: 'Retire an old fridge responsibly',
          },
        ],
      },
    ],
    featured: {
      title: 'Appliance Price Index',
      description: 'How refrigeration prices move over time, built from our own catalog.',
      // Owner directive 07/20 (item 12): every menu's featured image must be
      // DISTINCT and topically correct. Data previously reused
      // specialty-featured.jpg (shared with Local). Now its own on-brand
      // data-portal graphic (light bg, Fridge.com wordmark in the artwork).
      image: `/menu-images/data-featured.jpg`,
      href: '/data',
      cta: 'Explore the data',
    },
  },

  // ─────────────────────────────────────────────
  // 7. TOOLS — 89 calculators & widgets (NEW)
  // ─────────────────────────────────────────────
  {
    trigger: 'Tools',
    href: '/tools',
    sections: [
      {
        label: 'Calculators',
        items: [
          {
            title: 'Energy Cost Calculator',
            href: '/tools/energy-cost-calculator',
            description: 'Annual running cost estimate',
          },
          {
            title: 'Find Your Fridge',
            href: '/tools/find-your-fridge',
            description: 'Product-group discovery quiz',
          },
          {
            title: 'Size Calculator',
            href: '/tools/refrigerator-size-calculator',
            description: 'Right fit for your kitchen',
          },
          {
            title: 'Kitchen Space Planner',
            href: '/tools/kitchen-space-planner',
            description: 'Measure your layout perfectly',
          },
          {
            title: 'Lifespan Calculator',
            href: '/tools/refrigerator-lifespan-calculator',
            description: 'How long will it last?',
          },
          {
            title: 'Wine Cooler Calculator',
            href: '/tools/wine-refrigerator-calculator',
            description: 'Perfect capacity for your bottles',
          },
        ],
      },
      {
        label: 'Developer Access',
        items: [
          {
            title: 'Embeddable Widgets',
            href: '/widgets',
            description: 'Ninety free interactive tools for your site',
          },
          {
            title: 'API Access',
            href: '/contact',
            description: 'Contact us for data access',
          },
        ],
      },
      {
        label: 'All Tools',
        items: [
          {
            title: 'Browse All 90 Tools',
            href: '/tools',
            description: 'Calculators, guides & more',
          },
        ],
      },
    ],
    featured: {
      title: 'Energy Cost Calculator',
      description: 'Calculate the annual and lifetime running cost of any appliance.',
      image: `/menu-images/commercial-featured.jpg`,
      href: '/tools/energy-cost-calculator',
      cta: 'Calculate Cost',
    },
  },
]

// Simple links (no dropdown) — empty, all navigation is via mega menus
export const simpleLinks: { title: string; href: string }[] = []
